import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  List,
  Paper
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { api } from '../../utils/api';

const SubtaskDialog = ({ open, onClose, item, onSubtasksCreated }) => {
  const [subtasks, setSubtasks] = useState([
    { title: '', description: '', estimate: '', estimateType: 'none' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubtaskChange = (index, field, value) => {
    const updated = [...subtasks];
    updated[index][field] = value;
    setSubtasks(updated);
    if (error) setError(null);
  };

  const addSubtask = () => {
    setSubtasks([
      ...subtasks,
      { title: '', description: '', estimate: '', estimateType: 'none' }
    ]);
  };

  const removeSubtask = (index) => {
    if (subtasks.length > 1) {
      const updated = subtasks.filter((_, i) => i !== index);
      setSubtasks(updated);
    }
  };

  const resetForm = () => {
    setSubtasks([{ title: '', description: '', estimate: '', estimateType: 'none' }]);
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate at least one subtask has a title
    const validSubtasks = subtasks.filter(s => s.title.trim());
    if (validSubtasks.length === 0) {
      setError('At least one subtask title is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/items/${item.id}/subtasks`, {
        subtasks: validSubtasks.map(s => ({
          title: s.title.trim(),
          description: s.description.trim() || null,
          estimate: s.estimate || null,
          estimateType: s.estimateType
        }))
      });

      if (response.data) {
        onSubtasksCreated(response.data);
        resetForm();
        onClose();
      }
    } catch (err) {
      console.error('Failed to create subtasks:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to create subtasks.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validCount = subtasks.filter(s => s.title.trim()).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Add Subtasks</Typography>
            <Typography variant="body2" color="text.secondary">
              {item?.title} {item?.jiraKey && `(${item.jiraKey})`}
            </Typography>
          </Box>
          {item?.finalEstimate && (
            <Typography variant="body2" color="primary">
              Story Points: {item.finalEstimate}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List sx={{ p: 0 }}>
          {subtasks.map((subtask, index) => (
            <Paper key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box flex={1}>
                  <TextField
                    autoFocus={index === 0}
                    size="small"
                    label={`Subtask ${index + 1} Title`}
                    fullWidth
                    value={subtask.title}
                    onChange={(e) => handleSubtaskChange(index, 'title', e.target.value)}
                    required
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Description (Optional)"
                    fullWidth
                    multiline
                    rows={2}
                    value={subtask.description}
                    onChange={(e) => handleSubtaskChange(index, 'description', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box display="flex" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Estimate Type</InputLabel>
                      <Select
                        value={subtask.estimateType}
                        label="Estimate Type"
                        onChange={(e) => handleSubtaskChange(index, 'estimateType', e.target.value)}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="story_points">Story Points</MenuItem>
                      </Select>
                    </FormControl>
                    {subtask.estimateType !== 'none' && (
                      <TextField
                        size="small"
                        label={subtask.estimateType === 'hours' ? 'Hours' : 'Points'}
                        type="number"
                        value={subtask.estimate}
                        onChange={(e) => handleSubtaskChange(index, 'estimate', e.target.value)}
                        sx={{ width: 100 }}
                        inputProps={{ min: 0, step: subtask.estimateType === 'hours' ? 0.5 : 1 }}
                      />
                    )}
                  </Box>
                </Box>
                <IconButton
                  onClick={() => removeSubtask(index)}
                  disabled={subtasks.length === 1}
                  color="error"
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </List>

        <Button
          startIcon={<Add />}
          onClick={addSubtask}
          variant="outlined"
          size="small"
          fullWidth
        >
          Add Another Subtask
        </Button>
      </DialogContent>

      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading || validCount === 0}
            startIcon={<Save />}
          >
            Save {validCount > 0 ? `${validCount} Subtask${validCount > 1 ? 's' : ''}` : 'Subtasks'}
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubtaskDialog;
